import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Clock, Quote, ChevronLeft, ChevronRight } from "lucide-react";

type Metric = { value: string; label: string };
type Study = {
  id: string;
  tab: string;
  industry: string;
  location: string;
  timeframe: string;
  service: string;
  headline: string;
  quote: string;
  metrics: [Metric, Metric, Metric];
};

const studies: Study[] = [
  {
    id: "plumbing",
    tab: "Plumbing",
    industry: "Plumbing business",
    location: "Suburban Sydney",
    timeframe: "4 months",
    service: "Reviews + Local SEO",
    headline: "Steady lift in calls and a fuller weekly schedule",
    quote: "We're getting a steadier flow of work now and the diary fills up earlier in the week. Feels a lot more predictable.",
    metrics: [
      { value: "+62%", label: "More calls and enquiries from Google" },
      { value: "Page 1", label: "First-page visibility for key local searches" },
      { value: "+34", label: "New 5-star reviews in the first month" },
    ],
  },
  {
    id: "electrical",
    tab: "Electrical",
    industry: "Domestic electrician",
    location: "Brisbane",
    timeframe: "3 months",
    service: "Website + Reviews + Missed-call text-back",
    headline: "More consistent enquiries and fewer missed jobs",
    quote: "Phone is ringing more often and the missed-call text-back has saved a heap of jobs we would've lost.",
    metrics: [
      { value: "+48%", label: "Lift in monthly website enquiries" },
      { value: "+22", label: "New 5-star reviews in 35 days" },
      { value: "1 in 3", label: "Missed calls recovered via text-back" },
    ],
  },
  {
    id: "cleaning",
    tab: "Cleaning",
    industry: "Residential cleaning company",
    location: "Melbourne",
    timeframe: "5 months",
    service: "Reviews + Local SEO",
    headline: "Stronger reputation and better visibility on Google",
    quote: "Our review profile looks completely different now and new customers find us much more easily.",
    metrics: [
      { value: "3.4 → 4.7★", label: "Google rating lifted over time" },
      { value: "+58", label: "New 5-star reviews in 2 months" },
      { value: "+71%", label: "Increase in Google profile views" },
    ],
  },
  {
    id: "salon",
    tab: "Hair Salon",
    industry: "Hair salon",
    location: "Brisbane",
    timeframe: "4 months",
    service: "Website + Local SEO + Reviews",
    headline: "More walk-ins and a healthier booking calendar",
    quote: "We're seeing more new faces each week and the calendar fills up further out than it used to.",
    metrics: [
      { value: "+38%", label: "Lift in walk-ins and online bookings" },
      { value: "+45", label: "New 5-star reviews over 4 months" },
      { value: "Page 1", label: "Appearing on first page for key local searches" },
    ],
  },
  {
    id: "builder",
    tab: "Builder",
    industry: "Custom home builder",
    location: "Gold Coast",
    timeframe: "6 months",
    service: "Website + Lead Generation ads",
    headline: "Better quality enquiries and a healthier pipeline",
    quote: "The enquiries we're getting are far more aligned with the work we want to take on. Pipeline feels much healthier.",
    metrics: [
      { value: "8–12", label: "Pre-qualified enquiries each month" },
      { value: "1 in 4", label: "Quote close rate (up from 1 in 8)" },
      { value: "+52%", label: "More website traffic from local searches" },
    ],
  },
  {
    id: "pest",
    tab: "Pest Control",
    industry: "Pest control business",
    location: "Perth",
    timeframe: "4 months",
    service: "Lead Generation ads only",
    headline: "Off shared lead marketplaces and onto exclusive enquiries",
    quote: "We're no longer fighting other businesses for the same lead. The enquiries are ours and the cost is much more sustainable.",
    metrics: [
      { value: "−60%", label: "Lower cost per enquiry vs marketplaces" },
      { value: "30+", label: "Exclusive enquiries each month" },
      { value: "+28%", label: "More recurring service plans" },
    ],
  },
];

export function CaseStudyTabs() {
  const [active, setActive] = useState(studies[0].id);
  const study = studies.find((s) => s.id === active)!;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollByDir = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
  };

  const goToStudy = (dir: 1 | -1) => {
    const idx = studies.findIndex((s) => s.id === active);
    const next = (idx + dir + studies.length) % studies.length;
    setActive(studies[next].id);
  };

  return (
    <section className="section-padding bg-background relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-25" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-primary/[0.04] rounded-full blur-[140px]" />

      <div className="container-wide relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Real Results</span>
          </div>
          <h2 className="heading-section text-foreground mb-3 text-balance">
            From <span className="text-gradient">invisible</span> to fully booked
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-[1.65]">
            Real outcomes from real businesses. Tap any industry to see what changed.
          </p>
        </motion.div>

        {/* Mobile: stepper with prev/next + active label */}
        <div className="sm:hidden mb-6">
          <div className="flex items-center justify-between gap-2 bg-card border border-border/60 rounded-full p-1.5 shadow-sm">
            <button
              type="button"
              onClick={() => goToStudy(-1)}
              aria-label="Previous case study"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-muted/60 text-foreground active:scale-95 transition-transform"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center min-w-0 px-2">
              <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold">
                {studies.findIndex((s) => s.id === active) + 1} / {studies.length}
              </div>
              <div className="text-sm font-bold text-foreground truncate">
                {study.tab}
              </div>
            </div>
            <button
              type="button"
              onClick={() => goToStudy(1)}
              aria-label="Next case study"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          {/* Dots indicator */}
          <div className="flex justify-center gap-1.5 mt-3">
            {studies.map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                aria-label={`Go to ${s.tab} case study`}
                className={`h-1.5 rounded-full transition-all ${
                  active === s.id ? "w-6 bg-primary" : "w-1.5 bg-border"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: tab strip with arrow controls */}
        <div className="hidden sm:block mb-9 relative">
          <button
            type="button"
            onClick={() => scrollByDir(-1)}
            aria-label="Scroll tabs left"
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-card border border-border/60 shadow-sm text-foreground hover:bg-muted transition-opacity ${canLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollByDir(1)}
            aria-label="Scroll tabs right"
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-card border border-border/60 shadow-sm text-foreground hover:bg-muted transition-opacity ${canRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <div
            ref={scrollerRef}
            className="px-12 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
          >
            <div className="flex justify-center gap-1 min-w-max border-b border-border/60">
              {studies.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`relative px-5 pb-3 pt-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                    active === s.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {s.tab}
                  {active === s.id && (
                    <motion.span
                      layoutId="case-study-underline"
                      className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary rounded-full"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.article
            key={study.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-card rounded-3xl border border-border/60 shadow-card overflow-hidden">
              {/* Top: meta + headline */}
              <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 sm:pb-8">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[0.75rem] sm:text-[0.8125rem] text-muted-foreground mb-3">
                  <span className="font-semibold text-foreground/80">{study.industry}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                    {study.location}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    Results over {study.timeframe}
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-[0.75rem] sm:text-[0.8125rem] font-semibold text-primary">
                  Service: {study.service}
                </div>
                <h3 className="text-[1.375rem] sm:text-[1.75rem] md:text-[2rem] font-bold text-foreground leading-[1.15] tracking-[-0.02em] text-balance max-w-3xl">
                  {study.headline}
                </h3>
              </div>

              {/* Metrics — 3 big numbers */}
              <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-border/60 divide-y sm:divide-y-0 sm:divide-x divide-border/60 bg-gradient-to-b from-muted/30 to-transparent">
                {study.metrics.map((m, i) => (
                  <motion.div
                    key={m.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
                    className="px-6 sm:px-8 py-6 sm:py-7 text-center sm:text-left"
                  >
                    <div className="text-[2rem] sm:text-[2.5rem] font-extrabold text-gradient leading-none tracking-[-0.03em] mb-2">
                      {m.value}
                    </div>
                    <div className="text-[0.8125rem] sm:text-[0.875rem] text-muted-foreground font-medium leading-snug">
                      {m.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Quote */}
              <div className="px-6 sm:px-10 py-6 sm:py-7 border-t border-border/60 flex gap-4">
                <Quote className="w-6 h-6 sm:w-7 sm:h-7 text-primary/30 flex-shrink-0 -scale-x-100" />
                <p className="text-[0.9375rem] sm:text-base text-foreground/85 italic leading-[1.65]">
                  {study.quote}
                </p>
              </div>
            </div>

            {/* Footer link */}
            <div className="text-center mt-6">
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm hover:gap-2.5 transition-all"
              >
                See all case studies
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>
    </section>
  );
}
